import useMergedRef from '@react-hook/merged-ref';
import { NavbarElement } from '@typedream/core';
import React, {
  PropsWithChildren, useContext, useRef, useState,
} from 'react';
import { DropTargetMonitor, useDrop } from 'react-dnd';
import { Floating } from '../../../../components/Floating';
import { OverlayPortal } from '../../../../overlay/OverlayPortal';
import { useTPlateEditorRef } from '../../../../plate';
import { DropLine } from '../../../../plugins/dnd/components/DropLine';
import { DragItem } from '../../../../plugins/dnd/types';
import { NavbarMenuItemContext } from '../../context/navbar.context';
import { NavbarDropdownSelectedItemContext } from '../../context/navbarDropdownSelectedItem.context';
import { DropdownMenuDragHandle } from './DropdownMenuDragHandle';

type ElementBoxProps = PropsWithChildren<{
  element: NavbarElement,
  menuIndex: number,
  endMenu: boolean,
  columnId?: string,
  columnIdx?: number,
  columnItemIdx?: number,
  columnItemId?: string,
  styles?: React.CSSProperties,
}>;

export type DropDirection = 'top' | 'bottom' | 'left' | 'right';

const getDropDirection = (
  monitor: DropTargetMonitor,
  dropTargetElement: HTMLElement,
): DropDirection | void => {
  const clientOffset = monitor.getClientOffset();
  if (!clientOffset) return undefined;

  const dropTargetRect = dropTargetElement.getBoundingClientRect();

  const hoverMiddleY = (dropTargetRect.bottom - dropTargetRect.top) / 2;
  const hoverClientY = clientOffset.y - dropTargetRect.top;

  // use horizontal direction when hovering close to the left or right edges
  // the value is set to min 25% width or max 30px to the left or right
  const leftEdge = dropTargetRect.x + Math.min(30, 0.25 * dropTargetRect.width);
  const rightEdge = dropTargetRect.x
    + Math.max(dropTargetRect.width - 30, 0.75 * dropTargetRect.width);

  if (clientOffset.x < leftEdge) return 'left';
  if (clientOffset.x > rightEdge) return 'right';
  return hoverClientY < hoverMiddleY ? 'top' : 'bottom';
};

export function DropdownElementBox(props: ElementBoxProps) {
  const {
    element,
    menuIndex,
    children,
    endMenu,
    columnId,
    columnItemId,
    // EXPERIMENTAL
    columnIdx,
    columnItemIdx,
    styles,
  } = props;
  const { element: navbar } = useContext(NavbarMenuItemContext);
  const editor = useTPlateEditorRef();
  const { id, setId } = useContext(NavbarDropdownSelectedItemContext);

  const [dropDirection, setDropDirection] = useState<DropDirection>();
  const dropDirectionRef = useRef<DropDirection>();

  // Could be one of `navbar_dropdown_menu_column`, `navbar_dropdown_menu_item`,
  // or `navbar_dropdown_menu`
  const menuRef = useRef<HTMLDivElement>(null);

  const onMouseOver = (e: React.MouseEvent) => {
    e.stopPropagation();
    setId(element.id);
  };

  const onMouseLeave = (e: React.MouseEvent) => {
    e.stopPropagation();
    setId('');
  };

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    editor.emitter.emit('toolbar:navbar:dropdown-menu-item:hover', {
      ref: menuRef?.current,
      state: true,
      nav: navbar,
      navbarMenuItemIndex: menuIndex,
      navbarMenuItemType: endMenu ? 'endMenu' : 'menu',
      dropdownItemType: element.type === 'navbar_dropdown_menu_column' ? 'column' : 'column_item',
      columnId,
      columnItemId,
    });
  };

  const resolveAcceptedDrop = () => {
    if (element.type === 'navbar_dropdown_menu_column') {
      return ['navbar_dropdown_menu_column'];
    }

    if (element.type === 'navbar_dropdown_menu_item') {
      return ['navbar_dropdown_menu_item'];
    }

    return [];
  };

  const [{ canDrop, isOver }, dropTarget] = useDrop(() => ({
    // The type (or types) to accept - strings or symbols
    // accept: resolveAcceptedDrop(),
    accept: resolveAcceptedDrop(),
    // Props to collect
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    drop: (item) => {
      if ('columnItemId' in (item as { columnItemId?: string, columnId?: string })) {
        // eslint-disable-next-line max-len
        const dragSource: { columnItemId?: string, columnId?: string } = item as { columnItemId?: string, columnId?: string };
        editor.emitter.emit('toolbar:navbar:dropdown-menu-item:drop', {
          navbarMenuItemType: endMenu ? 'endMenu' : 'menu',
          navbarMenuItemIndex: menuIndex,
          // Properties to identify the drop target
          dropTargetId: columnItemId,
          dropTargetColumnId: columnId,
          dropDirection,
          // Properties to identify the dropdown menu item
          dragSourceId: dragSource.columnItemId,
          dragSourceColumnId: dragSource.columnId,
        });
      } else {
        const dragSource: { columnId?: string } = item as { columnId?: string };
        editor.emitter.emit('toolbar:navbar:dropdown-column:drop', {
          navbarMenuItemType: endMenu ? 'endMenu' : 'menu',
          navbarMenuItemIndex: menuIndex,
          // Properties to identify the drop target
          dropTargetColumnId: columnId,
          dropDirection,
          // Properties to identify the dropdown menu item
          dragSourceColumnId: dragSource.columnId,
        });
      }
    },
    hover: (_, monitor) => {
      if (!menuRef || !menuRef?.current) return;

      const dir = getDropDirection(monitor, menuRef.current);
      if (!dir) return;

      setDropDirection(dir);
      dropDirectionRef.current = dir;
    },
  }), [element, dropDirection]);

  const containerRef = useMergedRef(dropTarget, menuRef);

  return (
    <div
      contentEditable={false}
      id={element.id}
      tabIndex={0}
      role="button"
      ref={containerRef}
      onMouseOver={(e) => onMouseOver(e)}
      onFocus={() => { }}
      onMouseLeave={onMouseLeave}
      style={{
        position: 'relative',
        ...styles,
      }}
      onClick={onClick}
      onKeyDown={() => { }}
    >
      {children}
      {
        id === element.id
        && (
          <div style={{
            pointerEvents: 'none',
            inset: 0,
            position: 'absolute',
            top: 0,
            left: 0,
            width: menuRef?.current?.offsetWidth ?? 0,
            height: menuRef?.current?.offsetHeight ?? 0,
            outline: '1px solid var(--background-highlight-highlight)',
          }}
          >
            <DropdownMenuDragHandle
              columnId={columnId}
              columnItemId={columnItemId}
              element={element}
              reference={menuRef}
            />
          </div>
        )
      }
      {isOver && canDrop && menuRef && (
        <OverlayPortal>
          <Floating
            reference={menuRef}
            cover
            autoUpdate
            style={{
              backgroundColor: 'var(--dropBackground)',
            }}
          >
            {dropDirection && <DropLine direction={dropDirection} />}
          </Floating>
        </OverlayPortal>
      )}
    </div>
  );
}

DropdownElementBox.defaultProps = {
  columnId: undefined,
  columnItemId: undefined,
  columnIdx: undefined,
  columnItemIdx: undefined,
  styles: undefined,
};
